import { create } from 'zustand';
import { SessionListParam } from '@mygpt/types';

type PageSource = 'widget' | 'playground' | 'share';

export const DEFAULT_CANVAS_TITLE = 'Canvas';

interface CanvasContent {
  type: 'url' | 'pdf';
  url?: string;
  title: string;
  snapPointIndex?: number; // 移动端 Canvas 默认打开时的 snapPoint 下标，目前只有0、1，取决于 MobileSheet 的 snapPoints 数组
  // PDF-specific properties
  refData?: {
    file_id: string;
    source: string;
    texts?: string[];
    pages?: number[];
  };
}

interface ChatStoreStatus {
  pageSource: PageSource | undefined;
  setPageSource: (pageSource: PageSource) => void;

  // 需要展示的消息 Canvas
  // PC 端因为是弹框的交互，同时只能存在一个
  // 移动端是嵌入在消息的下面，同时可以打开多个 Canvas
  shownCanvas: {
    [messageId: string]: CanvasContent;
  };
  addShownCanvas: (messageId: string, url: string, title: string, snapPointIndex?: number) => void;
  removeShownCanvas: (messageId: string) => void;
  // 覆盖性设置 Canvas
  setShownCanvas: (messageId: string, url: string, title: string, snapPointIndex?: number) => void;
  // 设置 PDF 模式的 Canvas
  setShownPdfCanvas: (refData: { file_id: string; source: string; texts?: string[]; pages?: number[] }) => void;
  clearShownCanvas: () => void;

  ttsMuted: boolean;
  setTtsMuted: (ttsMuted: boolean) => void;

  // Agent 类型 bot 的 tool call 和 tool response 详情显示控制
  showToolDetails: boolean;
  toggleShowToolDetails: () => void;

  // 当前聊天消息（供 TransferToHumanButton 读取）
  chatMessages: SessionListParam[];
  setChatMessages: (messages: SessionListParam[]) => void;

  // 发送消息回调（由 Chat 组件注册，供 TransferToHumanButton 调用）
  submitMessage: ((aiInstruction: string, displayText?: string) => void) | null;
  setSubmitMessage: (fn: ((aiInstruction: string, displayText?: string) => void) | null) => void;
}

export const useChatStore = create<ChatStoreStatus>((set) => ({
  pageSource: undefined,
  shownCanvas: {},
  setPageSource: (pageSource) => set({ pageSource }),
  addShownCanvas: (messageId: string, url: string, title: string, snapPointIndex?: number) => {
    set((state) => ({
      shownCanvas: {
        ...state.shownCanvas,
        [messageId]: { type: 'url', url, title, snapPointIndex: snapPointIndex || 0 },
      },
    }));
  },
  removeShownCanvas: (messageId: string) => {
    set((state) => ({
      shownCanvas: Object.fromEntries(Object.entries(state.shownCanvas).filter(([key]) => key !== messageId)),
    }));
  },
  setShownCanvas: (messageId: string, url: string, title: string, snapPointIndex?: number) => {
    set(() => ({ shownCanvas: { [messageId]: { type: 'url', url, title, snapPointIndex: snapPointIndex || 0 } } }));
  },
  setShownPdfCanvas: (refData: { file_id: string; source: string; texts?: string[]; pages?: number[] }) => {
    set(() => ({
      shownCanvas: {
        [refData.file_id]: {
          type: 'pdf',
          refData: refData,
        },
      },
    }));
  },
  clearShownCanvas: () => {
    set({ shownCanvas: {} });
  },
  ttsMuted: false,
  setTtsMuted: (ttsMuted: boolean) => {
    set({ ttsMuted });
  },
  showToolDetails: false,
  toggleShowToolDetails: () => set((state) => ({ showToolDetails: !state.showToolDetails })),
  chatMessages: [],
  setChatMessages: (messages) => set({ chatMessages: messages }),
  submitMessage: null,
  setSubmitMessage: (fn) => set({ submitMessage: fn }),
}));
